import React, { useState } from "react";
import {
  Container,
  Title,
  Text,
  Box,
  Paper,
  Divider,
  List,
  ThemeIcon,
  Group,
  Anchor,
  Button,
  Accordion,
  ScrollArea,
  CheckIcon,
} from "@mantine/core";
import dottedListStyle from "./TermsOfService.module.css";

// import { Check, ChevronUp } from 'lucide-react';

const TermsOfService = () => {
  const [activeSection, setActiveSection] = useState(null);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <Container size="lg" py="xl">
      <Paper shadow="xs" p="md" withBorder className="mb-8">
        <Title order={1} className="text-center text-2xl font-bold mb-2">
          SOCIUS LEGAL CORPORATION TERMS OF SERVICE
        </Title>
        <Text className="text-center text-sm">
          Date of Last Revision: January 21, 2025
        </Text>
      </Paper>

      <Box className="mb-6">
        <Title order={2} className="text-xl font-bold mb-4">
          Acceptance of These Terms of Service
        </Title>
        <Text className="mb-4">
          Socius Legal Corporation ("Socius," "we," "us," or "our") provides our
          services (described below) and related content to you through our
          website located at www.sociuslegal.com (the "Site") and through our
          email interface and related technologies (collectively with the Site,
          including any updated or new features, functionality, outputs and
          technology, the "Service"). All access and use of the Service is
          subject to the terms and conditions contained in these Terms of
          Service (as amended from time to time, these "Terms of Service"). By
          accessing, browsing, or otherwise using the Site or any other aspect
          of the Service, you acknowledge that you have read, understood, and
          agree to be bound by these Terms of Service. If you do not accept the
          terms and conditions of these Terms of Service, you will not access,
          browse, or otherwise use the Service.
        </Text>
        <Text className="mb-4">
          We reserve the right, at our sole discretion, to change or modify
          portions of these Terms of Service at any time. If we do this, we will
          post the changes on this page and will indicate at the top of this
          page the date these Terms of Service were last revised. You may read a
          current, effective copy of these Terms of Service by visiting the
          "Terms of Service" link on the Site. We will also notify you of any
          material changes, either through the Service user interface, a pop-up
          notice, email, or through other reasonable means. Your continued use
          of the Service after the date any such changes become effective
          constitutes your acceptance of the new Terms of Service. You should
          periodically visit this page to review the current Terms of Service so
          you are aware of any revisions. If you do not agree to abide by these
          or any future Terms of Service, you will not access, browse, or use
          (or continue to access, browse, or use) the Service.
        </Text>
      </Box>

      <Box className="mb-6">
        <Paper withBorder p="md" className="bg-gray-50">
          <Text className="font-bold">
            PLEASE READ THESE TERMS OF SERVICE CAREFULLY, AS THEY CONTAIN AN
            AGREEMENT TO ARBITRATE AND OTHER IMPORTANT INFORMATION REGARDING
            YOUR LEGAL RIGHTS, REMEDIES, AND OBLIGATIONS. THE AGREEMENT TO
            ARBITRATE REQUIRES (WITH LIMITED EXCEPTION) THAT YOU SUBMIT CLAIMS
            YOU HAVE AGAINST US TO BINDING AND FINAL ARBITRATION, AND FURTHER
            (1) YOU WILL ONLY BE PERMITTED TO PURSUE CLAIMS AGAINST SOCIUS ON AN
            INDIVIDUAL BASIS, NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY CLASS OR
            REPRESENTATIVE ACTION OR PROCEEDING, (2) YOU WILL ONLY BE PERMITTED
            TO SEEK RELIEF (INCLUDING MONETARY, INJUNCTIVE, AND DECLARATORY
            RELIEF) ON AN INDIVIDUAL BASIS, AND (3) YOU MAY NOT BE ABLE TO HAVE
            ANY CLAIMS YOU HAVE AGAINST US RESOLVED BY A JURY OR IN A COURT OF
            LAW.
          </Text>
        </Paper>
      </Box>

      <Box className="mb-6">
        <Title order={2} className="text-xl font-bold mb-4">
          Additional Terms
        </Title>
        <Text className="mb-4">
          In addition, when using certain features through the Service, you will
          be subject to any additional terms applicable to such features that
          may be posted on or within the Service from time to time. All such
          terms are hereby incorporated by reference into these Terms of
          Service.
        </Text>
      </Box>

      <Accordion variant="separated">
        <Accordion.Item value="service">
          <Accordion.Control>
            <Title order={2} className="text-xl font-bold">
              Access and Use of the Service
            </Title>
          </Accordion.Control>
          <Accordion.Panel>
            <Box className="mb-4">
              <Title order={3} className="text-lg font-bold mb-2">
                Service Description
              </Title>
              <Text className="mb-4">
                The Service is designed to provide artificial intelligence
                ("AI") powered tools specifically for licensed attorneys and
                their authorized legal professionals. The Service includes,
                without limitation, (i) document automation and analysis tools,
                (ii) legal research assistance, (iii) practice management
                features, and (iv) other AI-enabled capabilities designed to
                support the practice of law. The Service is intended solely for
                use in connection with the authorized practice of law by
                eligible users as defined in these Terms of Service.
              </Text>
              <Text className="mb-4">
                You acknowledge and agree that the Service is a technology
                platform that provides tools to assist in legal work and
                practice management. The Service is not a law firm and does not
                provide legal advice or legal services. All outputs, analysis,
                and content generated through the Service ("Service Outputs")
                are designed to supplement, not replace, the professional
                judgment of licensed attorneys. You are solely responsible for
                (a) reviewing and validating all Service Outputs, (b) exercising
                independent professional judgment in using the Service and
                Service Outputs, (c) ensuring compliance with applicable ethical
                obligations and rules of professional conduct, and (d)
                maintaining appropriate attorney-client relationships and
                duties.
              </Text>
              <Text className="mb-4">
                Your use of the Service must comply with all applicable laws,
                regulations, and rules governing the practice of law in your
                jurisdiction, including without limitation rules regarding
                confidentiality, attorney-client privilege, unauthorized
                practice of law, and use of artificial intelligence in legal
                practice. You agree to use the Service only in connection with
                authorized legal work performed under appropriate attorney
                supervision and in compliance with these Terms of Service.
              </Text>
            </Box>

            <Box className="mb-4">
              <Title order={3} className="text-lg font-bold mb-2">
                Eligibility and Registration
              </Title>
              <Text className="mb-4">
                The Service is restricted to (i) licensed attorneys in good
                standing and (ii) authorized legal professionals working at the
                direction and under the supervision of licensed attorneys. By
                accessing or using the Service, you represent and warrant that
                you are either a licensed attorney in good standing in your
                jurisdiction or an authorized legal professional as defined
                herein.
              </Text>
              <Text className="mb-4">
                Licensed attorneys represent and warrant that they (a) maintain
                an active law license with their relevant state bar(s), (b) are
                in compliance with all applicable rules of professional conduct,
                and (c) will immediately notify Socius if their license status
                changes or if they become subject to any disciplinary action.
                Upon request by Socius, licensed attorneys must provide their
                state bar number(s), confirm their good standing status, provide
                professional contact information, and complete Socius's
                authentication process.
              </Text>
              <Text className="mb-4">
                Authorized legal professionals include paralegals, legal
                secretaries, legal assistants, and other legal support staff who
                are employed by or contracted to work for a licensed attorney or
                law firm. Such professionals represent and warrant that they are
                working under the direct supervision of a licensed attorney who
                maintains an active account with the Service. Upon request by
                Socius, authorized legal professionals must provide their
                professional role and credentials, identify their supervising
                attorney(s), provide their business contact information, and
                obtain verification from their supervising attorney.
              </Text>
              <Text className="mb-4">
                Licensed attorneys are responsible for supervising all
                authorized legal professionals working under their direction and
                ensuring appropriate use of the Service by such professionals.
                Licensed attorneys must review and take responsibility for all
                work product generated through the Service by themselves and
                their authorized legal professionals, maintain compliance with
                applicable rules of professional conduct, and promptly remove
                access for any authorized legal professionals who leave their
                supervision.
              </Text>
              <Text className="mb-4">
                You agree to maintain the accuracy of your registration
                information and promptly update any changes to your status or
                eligibility. Socius reserves the right to verify your
                eligibility at any time, request additional documentation of
                your status, suspend or terminate access if eligibility cannot
                be verified, conduct periodic re-verification of user status,
                and remove access for any users who no longer meet eligibility
                requirements. Any false or misleading information provided
                during registration may result in immediate termination of your
                account.
              </Text>
              <Text className="mb-4">
                You acknowledge and agree that you will only use the Service in
                compliance with applicable laws and rules of professional
                conduct, and that Socius may report any unauthorized use or
                misuse of the Service to relevant authorities or state bars.
              </Text>
            </Box>

            <Box className="mb-4">
              <Title order={3} className="text-lg font-bold mb-2">
                Member Account, Password and Security
              </Title>
              <Text className="mb-4">
                You are responsible for maintaining the confidentiality of your
                password and account details, if any, and are fully responsible
                for any and all activities that occur under your password or
                account. You agree to (a) immediately notify Socius of any
                unauthorized use of your password or account or any other breach
                of security, and (b) ensure that you exit from your account at
                the end of each session when accessing the Service. Socius and
                its current and future affiliates (collectively, "Socius
                Affiliates") will not be liable for any loss or damage arising
                from your failure to comply with this paragraph.
              </Text>
            </Box>

            <Box className="mb-4">
              <Title order={3} className="text-lg font-bold mb-2">
                Modifications to Service
              </Title>
              <Text className="mb-4">
                Socius reserves the right to modify or discontinue, temporarily
                or permanently, the Service (or any part thereof) with or
                without notice. You agree that Socius and Socius Affiliates will
                not be liable to you or to any third party for any modification,
                suspension or discontinuance of the Service.
              </Text>
            </Box>

            <Box className="mb-4">
              <Title order={3} className="text-lg font-bold mb-2">
                General Practices Regarding Use and Storage
              </Title>
              <Text className="mb-4">
                You acknowledge that Socius may establish general practices and
                limits concerning use of the Service, including the maximum
                period of time that data or other content will be retained by
                the Service and the maximum storage space that will be allotted
                on Socius's or its third-party service providers' servers on
                your behalf. You agree that Socius has no responsibility or
                liability for the deletion or failure to store any data or other
                content maintained or uploaded by the Service. You acknowledge
                that Socius reserves the right to terminate accounts that are
                inactive for an extended period of time. You further acknowledge
                that Socius reserves the right to change these general practices
                and limits at any time, in its sole discretion, with or without
                notice.
              </Text>
            </Box>
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="conditions">
          <Accordion.Control>
            <Title order={2} className="text-xl font-bold">
              Conditions of Access and Use
            </Title>
          </Accordion.Control>
          <Accordion.Panel>
            <Box className="mb-4">
              <Title order={3} className="text-lg font-bold mb-2">
                User Conduct
              </Title>
              <Text className="mb-4">
                You are solely responsible for all content, data, information,
                documents, communications, images, text and other materials
                ("content") that you make available to Socius, including by
                uploading, posting, publishing, emailing or displaying
                (hereinafter, "upload(ing)") via the Service (collectively,
                "User Content"). The following are examples of the kinds of
                content and/or uses that are illegal or prohibited by Socius.
                Socius reserves the right to monitor, investigate and take
                appropriate legal action against anyone who, in Socius's sole
                discretion, violates this provision, including removing the
                offending content from the Service, suspending or terminating
                the account of such violators, and reporting the violator to law
                enforcement authorities or regulators. You agree to not use the
                Service to upload any content that:
              </Text>
              <List
                spacing="xs"
                size="sm"
                center
                icon={
                  <ThemeIcon color="red" size={20} radius="xl">
                    <CheckIcon size={12} />
                  </ThemeIcon>
                }
              >
                <List.Item>
                  contains confidential information of any client without
                  appropriate authorization;
                </List.Item>
                <List.Item>
                  violates any duty of confidentiality or attorney-client
                  privilege;
                </List.Item>
                <List.Item>
                  infringes any intellectual property or other proprietary
                  rights;
                </List.Item>
                <List.Item>
                  contains software viruses or other harmful computer code;
                </List.Item>
                <List.Item>
                  poses a privacy or security risk to any person;
                </List.Item>
                <List.Item>
                  is unlawful, harmful, threatening, abusive, harassing,
                  defamatory, or otherwise objectionable;
                </List.Item>
                <List.Item>
                  contains personal identifying information of any individual
                  without appropriate authorization;
                </List.Item>
                <List.Item>
                  you do not have a right to upload under any law or under
                  contractual or fiduciary relationships;
                </List.Item>
                <List.Item>
                  constitutes unsolicited or unauthorized advertising,
                  promotional materials, commercial activities and/or sales,
                  "junk mail," "spam," "chain letters," "pyramid schemes,"
                  "contests," "sweepstakes," or any other form of solicitation;
                  or
                </List.Item>
                <List.Item>
                  in the sole judgment of Socius, restricts or inhibits any
                  other person from using the Service, or which may expose
                  Socius or its users to any harm or liability of any type.
                </List.Item>
              </List>

              <Text className="my-4">
                You agree to not use the Service to engage in any conduct that:
              </Text>
              <List
                spacing="xs"
                size="sm"
                center
                icon={
                  <ThemeIcon color="red" size={20} radius="xl">
                    <CheckIcon size={12} />
                  </ThemeIcon>
                }
              >
                <List.Item>
                  violates any applicable local, state, national, or
                  international law, or any regulations having the force of law,
                  or rule of professional conduct;
                </List.Item>
                <List.Item>
                  interferes with or disrupts the Service or servers or networks
                  connected to the Service, or disobeys any requirements,
                  procedures, policies, or regulations of networks connected to
                  the Service;
                </List.Item>
                <List.Item>
                  imposes an unreasonable load on our infrastructure;
                </List.Item>
                <List.Item>
                  attempts to gain unauthorized access to the Service;
                </List.Item>
                <List.Item>modifies, adapts, or hacks the Service;</List.Item>
                <List.Item>falsely implies association with Socius;</List.Item>
                <List.Item>
                  uses the Service for unauthorized practice of law;
                </List.Item>
                <List.Item>
                  impersonates any person or entity, or falsely state or
                  otherwise misrepresent your affiliation with a person or
                  entity;
                </List.Item>
                <List.Item>
                  furthers or promotes any criminal activity or enterprise or
                  provide instructional information about illegal activities;
                </List.Item>
                <List.Item>
                  obtains or attempts to access or otherwise obtain any content
                  or information through any means not intentionally made
                  available or provided for through the Service;
                </List.Item>
                <List.Item>
                  circumvents, removes, alters, deactivates, degrades, or
                  thwarts any of the content protections in or geographic
                  restrictions on any content (including Service Content (as
                  defined below)) available on or through the Service, including
                  through the use of virtual private networks; or
                </List.Item>
                <List.Item>
                  engages in or uses any data mining, robots, scraping, or
                  similar data gathering or extraction methods.
                </List.Item>
              </List>

              <Text className="my-4">
                You agree that your use of the Service will comply with all
                applicable rules of professional conduct and ethical
                obligations, including (without limitation):
              </Text>
              <List
                spacing="xs"
                size="sm"
                center
                icon={
                  <ThemeIcon color="blue" size={20} radius="xl">
                    <CheckIcon size={12} />
                  </ThemeIcon>
                }
              >
                <List.Item>
                  maintaining client confidentiality and protecting privileged
                  information;
                </List.Item>
                <List.Item>avoiding conflicts of interest;</List.Item>
                <List.Item>providing competent representation;</List.Item>
                <List.Item>
                  supervising subordinate attorneys and non-attorney staff;
                </List.Item>
                <List.Item>
                  making appropriate disclosures to clients regarding the use of
                  AI technology;
                </List.Item>
                <List.Item>
                  maintaining direct attorney-client relationships; and
                </List.Item>
                <List.Item>
                  exercising independent professional judgment.
                </List.Item>
              </List>

              <Text className="my-4">In using the Service, you agree to:</Text>
              <List
                spacing="xs"
                size="sm"
                center
                icon={
                  <ThemeIcon color="blue" size={20} radius="xl">
                    <CheckIcon size={12} />
                  </ThemeIcon>
                }
              >
                <List.Item>
                  implement appropriate security measures to protect client
                  data;
                </List.Item>
                <List.Item>
                  only upload client data with appropriate authorization;
                </List.Item>
                <List.Item>
                  promptly notify Socius of any security incidents or
                  unauthorized access;
                </List.Item>
                <List.Item>
                  maintain appropriate data retention and destruction policies;
                </List.Item>
                <List.Item>
                  comply with all applicable data privacy and security laws;
                </List.Item>
                <List.Item>
                  properly segregate and label confidential information; and
                </List.Item>
                <List.Item>
                  monitor and control access to client data by authorized users
                  under your supervision.
                </List.Item>
              </List>

              <Text className="my-4">You are responsible for:</Text>
              <List
                spacing="xs"
                size="sm"
                center
                icon={
                  <ThemeIcon color="blue" size={20} radius="xl">
                    <CheckIcon size={12} />
                  </ThemeIcon>
                }
              >
                <List.Item>
                  maintaining the confidentiality of your account credentials;
                </List.Item>
                <List.Item>
                  restricting access to your account to authorized individuals;
                </List.Item>
                <List.Item>
                  ensuring proper logout from the Service after each session;
                </List.Item>
                <List.Item>
                  notifying Socius immediately of any unauthorized access; and
                </List.Item>
                <List.Item>
                  being responsible for all activities that occur under your
                  account.
                </List.Item>
              </List>

              <Text className="my-4">
                If you are blocked by Socius from accessing the Service
                (including by blocking your IP address), you agree not to
                implement any measures to circumvent such blocking (e.g., by
                masking your IP address or using a proxy IP address or virtual
                private network).
              </Text>
            </Box>

            <Box className="mb-4">
              <Title order={3} className="text-lg font-bold mb-2">
                Competitors
              </Title>
              <Text className="mb-4">
                No employee, independent contractor, agent, or affiliate of any
                competing artificial technology and/or legal technology company
                is permitted to view, access, or use any portion of the Service
                without express written permission from Socius. By viewing,
                using, or accessing the Service, you represent and warrant that
                you are not a competitor of Socius or any Socius Affliate, or
                acting on behalf of a competitor of Socius or any Socius
                Affiliate in using or accessing the Service.
              </Text>
            </Box>

            <Box className="mb-4">
              <Title order={3} className="text-lg font-bold mb-2">
                Fees
              </Title>
              <Text className="mb-4">
                To the extent the Service or any portion thereof is made
                available for any fee, you may be required to select a payment
                plan and provide information regarding your credit card or other
                payment instrument. You represent and warrant to Socius that
                such information is true and that you are authorized to use the
                payment instrument. You will promptly update your account
                information with Socius or the Payment Processor (as defined
                below), as applicable, of any changes (for example, a change in
                your billing address or credit card expiration date) that may
                occur. You agree to pay Socius the amount that is specified in
                the payment plan in accordance with the terms of such plan and
                these Terms of Service. If your payment plan includes an ongoing
                subscription that is automatically renewed periodically, you
                hereby authorize Socius (through the Payment Processor) to bill
                your payment instrument in advance on such periodic basis in
                accordance with the terms of the applicable payment plan until
                you terminate your account, and you further agree to pay any
                charges so incurred. If you dispute any charges you must let
                Socius know within sixty (60) days after the date that Socius
                charges you, or within such longer period of time as may be
                required under applicable law. We reserve the right to change
                Socius's prices. If Socius does change prices, Socius will
                provide notice of the change through the Service user interface,
                a pop-up notice, email, or through other reasonable means, at
                Socius's option, at least thirty (30) days before the change is
                to take effect. Your continued use of the Service after the
                price change becomes effective constitutes your agreement to pay
                the changed amount. You will be responsible for all taxes
                associated with the Service, other than taxes based on Socius's
                net income.
              </Text>
            </Box>

            <Box className="mb-4">
              <Title order={3} className="text-lg font-bold mb-2">
                Payment Processing
              </Title>
              <Text className="mb-4">
                Notwithstanding any amounts owed to Socius hereunder, SOCIUS
                DOES NOT PROCESS PAYMENT FOR ANY SERVICES. To facilitate payment
                for the Service via bank account, credit card, or debit card, we
                use Stripe, Inc. and its affiliates ("Stripe"), a third-party
                payment processor. These payment processing services are
                provided by Stripe and are subject to the Stripe terms and
                conditions and other policies available at
                https://stripe.com/legal and Stripe's Global Privacy Policy
                available at: https://stripe.com/privacy (collectively, the
                "Stripe Agreements"), privacy policy, and all other relevant
                agreements (collectively, the "Payment Processor Agreements").
                By agreeing to these Terms of Service, users that use the
                payment functions of the Service also agree to be bound by the
                Stripe Agreements, as the same may be modified by Stripe from
                time to time. You hereby authorize Stripe to store and continue
                billing your specified payment method even after such payment
                method has expired, to avoid interruptions in payment for your
                use of the Service. Please contact Stripe for more information.
                Socius assumes no liability or responsibility for any payments
                you make through the Service.
              </Text>
            </Box>

            <Box className="mb-4">
              <Title order={3} className="text-lg font-bold mb-2">
                Refunds and Cancellations
              </Title>
              <Text className="mb-4">
                Payments made by you hereunder are final and non-refundable,
                unless otherwise determined by Socius. You may cancel your
                subscription by emailing us at: support@sociuslegal.com.
              </Text>
            </Box>

            <Box className="mb-4">
              <Title order={3} className="text-lg font-bold mb-2">
                Commercial Use
              </Title>
              <Text className="mb-4">
                Unless otherwise expressly authorized herein or in the Service,
                you agree not to display, distribute, license, perform, publish,
                reproduce, duplicate, copy, create derivative works from,
                modify, sell, resell, grant access to, transfer, or otherwise
                use or exploit any portion of the Service for any commercial
                purposes.
              </Text>
            </Box>
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="mobile">
          <Accordion.Control>
            <Title order={2} className="text-xl font-bold">
              Mobile Services and Software
            </Title>
          </Accordion.Control>
          <Accordion.Panel>
            <Box className="mb-4">
              <Title order={3} className="text-lg font-bold mb-2">
                Mobile Services
              </Title>
              <Text className="mb-4">
                The Service includes certain services that are available via a
                mobile device, including (i) the ability to upload content to
                the Service via a mobile device, (ii) the ability to browse the
                Service and the Site from a mobile device, and (iii) the ability
                to access certain features and content through a mobile device
                (collectively, the "Mobile Services"). To the extent you access
                the Service through a mobile device, your wireless service
                carrier's standard charges, data rates, and other fees may
                apply. In addition, downloading, installing, or using certain
                Mobile Services may be prohibited or restricted by your carrier,
                and not all Mobile Services may work with all carriers or
                devices.
              </Text>
            </Box>

            <Box className="mb-4">
              <Title order={3} className="text-lg font-bold mb-2">
                Ownership; Restrictions
              </Title>
              <Text className="mb-4">
                The technology, copyrighted material and software underlying the
                Service or distributed in connection therewith are the property
                of Socius, Socius Affiliates, and their licensors (the
                "Software"). You agree not to copy, modify, create a derivative
                work of, reverse engineer, reverse assemble, or otherwise
                attempt to discover any source code, sell, assign, sublicense,
                or otherwise transfer any right in the Software. Any rights not
                expressly granted herein are reserved by Socius.
              </Text>
            </Box>
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="intellectual">
          <Accordion.Control>
            <Title order={2} className="text-xl font-bold">
              Intellectual Property Rights
            </Title>
          </Accordion.Control>
          <Accordion.Panel>
            <Box className="mb-4">
              <Title order={3} className="text-lg font-bold mb-2">
                Service Content
              </Title>
              <Text className="mb-4">
                You acknowledge and agree that the Service may contain content
                or features, including all Service Outputs ("Service Content"),
                that are protected by copyright, patent, trademark, trade
                secret, or other proprietary rights and laws. Except as
                expressly authorized by Socius, you agree not to modify, copy,
                frame, scrape, rent, lease, loan, sell, distribute, or create
                derivative works based on the Service or the Service Content, in
                whole or in part, except that the foregoing does not apply to
                your own User Content (as defined below) that you upload to or
                make available through the Service in accordance with these
                Terms of Service. Any use of the Service or the Service Content
                other than as specifically authorized herein is strictly
                prohibited.
              </Text>
            </Box>

            <Box className="mb-4">
              <Title order={3} className="text-lg font-bold mb-2">
                Trademarks
              </Title>
              <Text className="mb-4">
                The Socius name and logos are trademarks and service marks of
                Socius (collectively the "Socius Trademarks"). Other Socius,
                product, and service names and logos used and displayed via the
                Service may be trademarks or service marks of their respective
                owners who may or may not endorse or be affiliated with or
                connected to Socius. Nothing in these Terms of Service or the
                Service should be construed as granting, by implication,
                estoppel, or otherwise, any license or right to use any of
                Socius Trademarks displayed on the Service, without our prior
                written permission in each instance. All goodwill generated from
                the use of Socius Trademarks will inure to our exclusive
                benefit.
              </Text>
            </Box>

            <Box className="mb-4">
              <Title order={3} className="text-lg font-bold mb-2">
                Third-Party Material
              </Title>
              <Text className="mb-4">
                Under no circumstances will Socius or any Socius Affiliate be
                liable in any way for any content or materials of any third
                parties (including users), including for any errors or omissions
                in any content, or for any loss or damage of any kind incurred
                as a result of the use of any such content. You acknowledge that
                Socius does not pre-screen content, but that Socius and its
                designees will have the right (but not the obligation) in their
                sole discretion to refuse or remove any content that is
                available via the Service. Without limiting the foregoing,
                Socius and its designees will have the right to remove any
                content that violates these Terms of Service or is deemed by
                Socius, in its sole discretion, to be otherwise objectionable.
                You agree that you must evaluate, and bear all risks associated
                with, the use of any content, including any reliance on the
                accuracy, completeness, or usefulness of such content.
              </Text>
            </Box>

            <Box className="mb-4">
              <Title order={3} className="text-lg font-bold mb-2">
                User Content
              </Title>
              <Text className="mb-4">
                You represent and warrant that you own all right, title and
                interest in and to such User Content, including all copyrights
                and rights of publicity contained therein. You hereby grant
                Socius and Socius Affiliates and, and their successors and
                assigns, a non-exclusive, worldwide, royalty-free, fully
                paid-up, transferable, sublicensable (directly and indirectly
                through multiple tiers), perpetual, and irrevocable license to
                copy, display, upload, perform, distribute, store, modify, and
                otherwise use your User Content, in any form, medium or
                technology now known or later developed, (a) in connection with
                the operation of the Service, (b) to provide, develop and
                improve the Service and other offerings of Socius, Socius
                Affiliates and/or their partners, (c) for the promotion,
                advertising or marketing of the foregoing. You also agree that
                Socius may remove metadata associated with your User Content,
                and you irrevocably waive any claims and assertions of moral
                rights or attribution with respect to your User Content. You
                assume all risk associated with your User Content and the
                transmission of your User Content, and you have sole
                responsibility for the accuracy, quality, legality and
                appropriateness of your User Content.
              </Text>
              <Text className="mb-4">
                You hereby authorize Socius and Socius Affiliates and their
                third-party service providers to collect and analyze User
                Content and other data and information relating to the Service
                and related systems and technologies and derive statistical and
                usage data relating thereto (collectively, "Usage Data"). Socius
                and Socius Affiliates may use Usage Data for any purpose in
                accordance with applicable law.
              </Text>
              <Text className="mb-4">
                Any questions, comments, suggestions, ideas, feedback, reviews,
                or other information about the Service ("Submissions"), provided
                by you to Socius or any Socius Affiliate are non-confidential
                and Socius and Socius Affiliates will be entitled to the
                unrestricted use and dissemination of these Submissions for any
                purpose, commercial or otherwise, without acknowledgment,
                attribution, or compensation to you.
              </Text>
              <Text className="mb-4">
                You acknowledge and agree that Socius may preserve User Content
                and may also disclose User Content if required to do so by law
                or in the good faith belief that such preservation or disclosure
                is reasonably necessary to: (a) comply with legal process,
                applicable laws, or government requests; (b) enforce these Terms
                of Service; (c) respond to claims that any content violates the
                rights of third parties; or (d) protect the rights, property, or
                personal safety of Socius, its users, or the public. You
                understand that the technical processing and transmission of the
                Service, including your User Content, may involve (i)
                transmissions over various networks; and (ii) changes to conform
                and adapt to technical requirements of connecting networks or
                devices.
              </Text>
            </Box>
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="indemnification">
          <Accordion.Control>
            <Title order={2} className="text-xl font-bold">
              Indemnification
            </Title>
          </Accordion.Control>
          <Accordion.Panel>
            <Text className="mb-4">
              To the extent permitted under applicable law, you agree to defend,
              indemnify, and hold harmless Socius and Socius Affiliates, and its
              and their respective officers, employees, directors, service
              providers, licensors, and agents (collectively, the "Socius
              Parties"), from any and all losses, damages, expenses, including
              reasonable attorneys' fees, rights, claims, actions of any kind,
              and injury (including death) arising out of or relating to your
              use of the Service, any User Content, your connection to the
              Service, your violation of these Terms of Service, or your
              violation of any rights of another. Socius will provide notice to
              you of any such claim, suit, or proceeding. Socius reserves the
              right to assume the exclusive defense and control of any matter
              which is subject to indemnification under this section, and you
              agree to cooperate with any reasonable requests assisting Socius's
              defense of such matter. You may not settle or compromise any claim
              against the Socius Parties without Socius's written consent.
            </Text>
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="disclaimer">
          <Accordion.Control>
            <Title order={2} className="text-xl font-bold">
              Disclaimer of Warranties
            </Title>
          </Accordion.Control>
          <Accordion.Panel>
            <Text className="mb-4 font-bold">
              YOUR USE OF THE SERVICE IS AT YOUR SOLE RISK. THE SERVICE IS
              PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS. THE SOCIUS
              PARTIES EXPRESSLY DISCLAIM ALL WARRANTIES OF ANY KIND, WHETHER
              EXPRESS, IMPLIED OR STATUTORY, INCLUDING THE IMPLIED WARRANTIES OF
              MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND
              NON-INFRINGEMENT.
            </Text>
            <Text className="mb-4 font-bold">
              THE SOCIUS PARTIES MAKE NO WARRANTY THAT (A) THE SERVICE WILL MEET
              YOUR REQUIREMENTS; (B) THE SERVICE WILL BE UNINTERRUPTED, TIMELY,
              SECURE, OR ERROR-FREE; (C) THE RESULTS THAT MAY BE OBTAINED FROM
              THE USE OF THE SERVICE WILL BE ACCURATE OR RELIABLE; OR (D) THE
              QUALITY OF ANY PRODUCTS, SERVICES, INFORMATION, OR OTHER MATERIAL
              PURCHASED OR OBTAINED BY YOU THROUGH THE SERVICE WILL MEET YOUR
              EXPECTATIONS.
            </Text>

            <Title order={3} className="text-lg font-bold mb-2 mt-6">
              Professional Services Disclaimers
            </Title>
            <Text className="mb-4 font-bold">
              THE SERVICE IS A TECHNOLOGY PLATFORM PROVIDED TO LICENSED
              ATTORNEYS AND THEIR AUTHORIZED LEGAL PROFESSIONALS. THE SOCIUS
              PARTIES DO NOT PRACTICE LAW OR PROVIDE LEGAL SERVICES. YOUR USE OF
              THE SERVICE DOES NOT CREATE AN ATTORNEY-CLIENT RELATIONSHIP WITH
              SOCIUS OR ANY SOCIUS AFFILIATE. YOU ACKNOWLEDGE AND AGREE THAT:
            </Text>
            <Text className="mb-4 font-bold">
              (I) THE SERVICE IS A TOOL TO ASSIST IN LEGAL WORK AND PRACTICE
              MANAGEMENT BUT DOES NOT REPLACE PROFESSIONAL LEGAL JUDGMENT;
            </Text>
            <Text className="mb-4 font-bold">
              (II) YOU, AS A LICENSED ATTORNEY OR AUTHORIZED LEGAL PROFESSIONAL
              WORKING UNDER ATTORNEY SUPERVISION, ARE SOLELY RESPONSIBLE FOR:
              (A) REVIEWING AND VALIDATING ALL SERVICE OUTPUTS; (B) ENSURING
              COMPLIANCE WITH APPLICABLE ETHICAL OBLIGATIONS; (C) MAINTAINING
              APPROPRIATE ATTORNEY-CLIENT RELATIONSHIPS; (D) EXERCISING
              INDEPENDENT PROFESSIONAL JUDGMENT; AND (E) DETERMINING THE
              APPROPRIATENESS OF USING THE SERVICE FOR ANY PARTICULAR MATTER;
            </Text>
            <Text className="mb-4 font-bold">
              (III) THE SOCIUS PARTIES MAKE NO REPRESENTATIONS OR WARRANTIES
              REGARDING THE LEGAL SUFFICIENCY, LEGAL ACCURACY, OR LEGAL
              COMPLIANCE OF ANY SERVICE OUTPUTS; AND
            </Text>
            <Text className="mb-4 font-bold">
              (IV) THE SERVICE IS NOT DESIGNED OR INTENDED TO BE USED FOR
              HIGH-RISK LEGAL MATTERS WHERE ERRORS COULD RESULT IN DEATH,
              PERSONAL INJURY, ENVIRONMENTAL DAMAGE, OR CATASTROPHIC FINANCIAL
              HARM.
            </Text>

            <Title order={3} className="text-lg font-bold mb-2 mt-6">
              Artificial Intelligence Disclaimers
            </Title>
            <Text className="mb-4 font-bold">
              THE SERVICE UTILIZES ARTIFICIAL INTELLIGENCE AND MACHINE LEARNING
              TECHNOLOGIES. YOU ACKNOWLEDGE AND AGREE THAT:
            </Text>
            <Text className="mb-4 font-bold">
              (I) AI-GENERATED CONTENT MAY CONTAIN ERRORS, OMISSIONS,
              INCONSISTENCIES, OR HALLUCINATIONS;
            </Text>
            <Text className="mb-4 font-bold">
              (II) AI MODELS MAY PRODUCE UNEXPECTED OR UNINTENDED OUTPUTS;
            </Text>
            <Text className="mb-4 font-bold">
              (III) AI TECHNOLOGY IS RAPIDLY EVOLVING AND MAY BE SUBJECT TO
              LIMITATIONS, BIASES, OR TECHNICAL CONSTRAINTS;
            </Text>
            <Text className="mb-4 font-bold">
              (IV) YOU ARE RESPONSIBLE FOR INDEPENDENTLY VERIFYING ANY
              AI-GENERATED CONTENT BEFORE RELYING ON IT FOR ANY LEGAL OR
              PROFESSIONAL PURPOSE; AND
            </Text>
            <Text className="mb-4 font-bold">
              (V) THE SOCIUS PARTIES DO NOT WARRANT THAT AI-GENERATED OUTPUTS
              WILL BE ERROR-FREE, COMPLETE, OR SUITABLE FOR YOUR INTENDED
              PURPOSE.
            </Text>

            <Title order={3} className="text-lg font-bold mb-2 mt-6">
              Regulatory and Compliance Disclaimers
            </Title>
            <Text className="mb-4 font-bold">
              YOU ACKNOWLEDGE THAT LAWS AND REGULATIONS GOVERNING THE PRACTICE
              OF LAW, USE OF AI IN LEGAL PRACTICE, AND ATTORNEY ETHICAL
              OBLIGATIONS:
            </Text>
            <Text className="mb-4 font-bold">(I) VARY BY JURISDICTION;</Text>
            <Text className="mb-4 font-bold">(II) MAY CHANGE OVER TIME;</Text>
            <Text className="mb-4 font-bold">
              (III) MAY IMPOSE SPECIFIC REQUIREMENTS OR RESTRICTIONS ON THE USE
              OF AI TOOLS;
            </Text>
            <Text className="mb-4 font-bold">
              (IV) MAY REQUIRE SPECIFIC DISCLOSURES OR CLIENT CONSENTS.
            </Text>
            <Text className="mb-4 font-bold">
              YOU ARE SOLELY RESPONSIBLE FOR:
            </Text>
            <Text className="mb-4 font-bold">
              (I) ENSURING YOUR USE OF THE SERVICE COMPLIES WITH APPLICABLE
              LAWS, REGULATIONS, AND ETHICAL OBLIGATIONS IN YOUR JURISDICTION;
            </Text>
            <Text className="mb-4 font-bold">
              (II) OBTAINING ANY NECESSARY CLIENT CONSENTS OR MAKING ANY
              REQUIRED DISCLOSURES REGARDING YOUR USE OF AI TOOLS;
            </Text>
            <Text className="mb-4 font-bold">
              (III) MAINTAINING APPROPRIATE DOCUMENTATION OF YOUR USE OF THE
              SERVICE;
            </Text>
            <Text className="mb-4 font-bold">
              (IV) IMPLEMENTING APPROPRIATE SAFEGUARDS TO PROTECT CLIENT
              CONFIDENTIALITY AND PRIVILEGED INFORMATION.
            </Text>
            <Text className="mb-4 font-bold">
              THE SOCIUS PARTIES MAKE NO REPRESENTATIONS OR WARRANTIES REGARDING
              THE COMPLIANCE OF THE SERVICE WITH ANY PARTICULAR LAW, REGULATION,
              OR ETHICAL REQUIREMENT IN ANY JURISDICTION.
            </Text>
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="limitation">
          <Accordion.Control>
            <Title order={2} className="text-xl font-bold">
              Limitation of Liability
            </Title>
          </Accordion.Control>
          <Accordion.Panel>
            <Text className="mb-4 font-bold">
              YOU EXPRESSLY UNDERSTAND AND AGREE THAT THE SOCIUS PARTIES WILL
              NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
              CONSEQUENTIAL, EXEMPLARY DAMAGES, OR DAMAGES FOR LOSS OF PROFITS
              INCLUDING DAMAGES FOR LOSS OF GOODWILL, USE, OR DATA OR OTHER
              INTANGIBLE LOSSES (EVEN IF THE SOCIUS PARTIES HAVE BEEN ADVISED OF
              THE POSSIBILITY OF SUCH DAMAGES), WHETHER BASED ON CONTRACT, TORT,
              NEGLIGENCE, STRICT LIABILITY, OR OTHERWISE, RESULTING FROM: (A)
              THE USE OR THE INABILITY TO USE THE SERVICE; (B) THE COST OF
              PROCUREMENT OF SUBSTITUTE GOODS AND SERVICES RESULTING FROM ANY
              GOODS, DATA, INFORMATION, OR SERVICES PURCHASED OR OBTAINED OR
              MESSAGES RECEIVED OR TRANSACTIONS ENTERED INTO THROUGH OR FROM THE
              SERVICE; (C) UNAUTHORIZED ACCESS TO OR ALTERATION OF YOUR
              TRANSMISSIONS OR DATA; (D) STATEMENTS OR CONDUCT OF ANY THIRD
              PARTY ON THE SERVICE; OR (E) ANY OTHER MATTER RELATING TO THE
              SERVICE. IN NO EVENT WILL THE SOCIUS PARTIES' TOTAL LIABILITY TO
              YOU FOR ALL DAMAGES, LOSSES, OR CAUSES OF ACTION EXCEED THE AMOUNT
              YOU HAVE PAID SOCIUS IN THE LAST SIX (6) MONTHS, OR, IF GREATER,
              ONE HUNDRED DOLLARS ($100).
            </Text>
            <Text className="mb-4">
              SOME JURISDICTIONS DO NOT ALLOW THE DISCLAIMER OR EXCLUSION OF
              CERTAIN WARRANTIES OR THE LIMITATION OR EXCLUSION OF LIABILITY FOR
              INCIDENTAL OR CONSEQUENTIAL DAMAGES. ACCORDINGLY, SOME OF THE
              ABOVE LIMITATIONS SET FORTH ABOVE MAY NOT APPLY TO YOU OR BE
              ENFORCEABLE WITH RESPECT TO YOU. IF YOU ARE DISSATISFIED WITH ANY
              PORTION OF THE SERVICE OR WITH THESE TERMS OF SERVICE, YOUR SOLE
              AND EXCLUSIVE REMEDY IS TO DISCONTINUE USE OF THE SERVICE.
            </Text>
            <Text className="mb-4">
              IF YOU ARE A USER FROM NEW JERSEY, THE FOREGOING SECTIONS TITLED
              "INDEMNIFICATION", "DISCLAIMER OF WARRANTIES" AND "LIMITATION OF
              LIABILITY" ARE INTENDED TO BE ONLY AS BROAD AS IS PERMITTED UNDER
              THE LAWS OF THE STATE OF NEW JERSEY. IF ANY PORTION OF THESE
              SECTIONS IS HELD TO BE INVALID UNDER THE LAWS OF THE STATE OF NEW
              JERSEY, THE INVALIDITY OF SUCH PORTION WILL NOT AFFECT THE
              VALIDITY OF THE REMAINING PORTIONS OF THE APPLICABLE SECTIONS.
            </Text>
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="dispute">
          <Accordion.Control>
            <Title order={2} className="text-xl font-bold">
              Dispute Resolution By Binding Arbitration
            </Title>
          </Accordion.Control>
          <Accordion.Panel>
            <Text className="mb-4 font-bold">
              PLEASE READ THIS SECTION CAREFULLY AS IT AFFECTS YOUR RIGHTS.
            </Text>

            <Title order={3} className="text-lg font-bold mb-2">
              Agreement to Arbitrate
            </Title>
            <Text className="mb-4">
              This Dispute Resolution by Binding Arbitration section is referred
              to in these Terms of Service as the "Arbitration Agreement." You
              agree that any and all disputes or claims that have arisen or may
              arise between you and Socius, whether arising out of or relating
              to these Terms of Service (including any alleged breach thereof),
              the Service, any advertising, or any aspect of the relationship or
              transactions between us, will be resolved exclusively through
              final and binding arbitration, rather than a court, in accordance
              with the terms of this Arbitration Agreement, except that you may
              assert individual claims in small claims court, if your claims
              qualify. Further, this Arbitration Agreement does not preclude you
              from bringing issues to the attention of federal, state, or local
              agencies, and such agencies can, if the law allows, seek relief
              against us on your behalf. You agree that, by entering into these
              Terms of Service, you and Socius are each waiving the right to a
              trial by jury or to participate in a class action. Your rights
              will be determined by a neutral arbitrator, not a judge or jury.
              The Federal Arbitration Act governs the interpretation and
              enforcement of this Arbitration Agreement.
            </Text>

            <Title order={3} className="text-lg font-bold mb-2">
              Prohibition of Class and Representative Actions and
              Non-Individualized Relief
            </Title>
            <Text className="mb-4 font-bold">
              YOU AND SOCIUS AGREE THAT EACH OF US MAY BRING CLAIMS AGAINST THE
              OTHER ONLY ON AN INDIVIDUAL BASIS AND NOT AS A PLAINTIFF OR CLASS
              MEMBER IN ANY PURPORTED CLASS OR REPRESENTATIVE ACTION OR
              PROCEEDING. UNLESS BOTH YOU AND SOCIUS AGREE OTHERWISE, THE
              ARBITRATOR MAY NOT CONSOLIDATE OR JOIN MORE THAN ONE PERSON'S OR
              PARTY'S CLAIMS AND MAY NOT OTHERWISE PRESIDE OVER ANY FORM OF A
              CONSOLIDATED, REPRESENTATIVE, OR CLASS PROCEEDING. ALSO, THE
              ARBITRATOR MAY AWARD RELIEF (INCLUDING MONETARY, INJUNCTIVE, AND
              DECLARATORY RELIEF) ONLY IN FAVOR OF THE INDIVIDUAL PARTY SEEKING
              RELIEF AND ONLY TO THE EXTENT NECESSARY TO PROVIDE RELIEF
              NECESSITATED BY THAT PARTY'S INDIVIDUAL CLAIM(S), EXCEPT THAT YOU
              MAY PURSUE A CLAIM FOR AND THE ARBITRATOR MAY AWARD PUBLIC
              INJUNCTIVE RELIEF UNDER APPLICABLE LAW TO THE EXTENT REQUIRED FOR
              THE ENFORCEABILITY OF THIS PROVISION.
            </Text>

            <Title order={3} className="text-lg font-bold mb-2">
              Pre-Arbitration Dispute Resolution
            </Title>
            <Text className="mb-4">
              Socius is always interested in resolving disputes amicably and
              efficiently, and most customer concerns can be resolved quickly
              and to the customer's satisfaction by emailing customer support at
              support@sociuslegal.com. If such efforts prove unsuccessful, a
              party who intends to seek arbitration must first send to the other
              a written Notice of Dispute ("Notice"). The Notice to Socius
              should be sent to support@sociuslegal.com ("Notice Address"). The
              Notice must (i) describe the nature and basis of the claim or
              dispute and (ii) set forth the specific relief sought. If Socius
              and you do not resolve the claim within sixty (60) calendar days
              after the Notice is received, you or Socius may commence an
              arbitration proceeding. During the arbitration, the amount of any
              settlement offer made by Socius or you will not be disclosed to
              the arbitrator until after the arbitrator determines the amount,
              if any, to which you or Socius is entitled.
            </Text>

            <Title order={3} className="text-lg font-bold mb-2">
              Arbitration Procedures
            </Title>
            <Text className="mb-4">
              Arbitration will be conducted by a neutral arbitrator in
              accordance with the American Arbitration Association's ("AAA")
              rules and procedures, including the AAA's Commercial Arbitration
              Rules and Consumer Arbitration Rules (collectively, the "AAA
              Rules"), as modified by this Arbitration Agreement. For
              information on the AAA, including the AAA Rules, please visit its
              website, https://www.adr.org. If there is any inconsistency
              between any term of the AAA Rules and any term of this Arbitration
              Agreement, the applicable terms of this Arbitration Agreement will
              control unless the arbitrator determines that the application of
              the inconsistent Arbitration Agreement terms would not result in a
              fundamentally fair arbitration. The arbitrator must also follow
              the provisions of these Terms of Service as a court would. All
              issues are for the arbitrator to decide, including issues relating
              to the scope, enforceability, and arbitrability of this
              Arbitration Agreement. Although arbitration proceedings are
              usually simpler and more streamlined than trials and other
              judicial proceedings, the arbitrator can award the same damages
              and relief on an individual basis that a court can award to an
              individual under these Terms of Service and applicable law.
              Decisions by the arbitrator are enforceable in court and may be
              overturned by a court only for very limited reasons.
            </Text>
            <Text className="mb-4">
              Unless Socius and you agree otherwise, any arbitration hearings
              will take place in a reasonably convenient location for both
              parties with due consideration of their ability to travel and
              other pertinent circumstances. If the parties are unable to agree
              on a location, the determination will be made by AAA. If your
              claim is for $10,000 or less, Socius agrees that you may choose
              whether the arbitration will be conducted solely on the basis of
              documents submitted to the arbitrator, through a telephonic
              hearing, or by an in-person hearing as established by the AAA
              Rules. If your claim exceeds $10,000, the right to a hearing will
              be determined by the AAA Rules. Regardless of the manner in which
              the arbitration is conducted, the arbitrator will issue a reasoned
              written decision sufficient to explain the essential findings and
              conclusions on which the award is based.
            </Text>

            <Title order={3} className="text-lg font-bold mb-2">
              Costs of Arbitration
            </Title>
            <Text className="mb-4">
              Payment of all filing, administration, and arbitrator fees
              (collectively, the "Arbitration Fees") will be governed by the AAA
              Rules, unless otherwise provided in this Arbitration Agreement. To
              the extent any Arbitration Fees are not specifically allocated to
              either Socius or you under the AAA Rules, Socius and you shall
              split them equally; provided that if you are able to demonstrate
              to the arbitrator that you are economically unable to pay your
              portion of such Arbitration Fees or if the arbitrator otherwise
              determines for any reason that you should not be required to pay
              your portion of any Arbitration Fees, Socius will pay your portion
              of such fees. In addition, if you demonstrate to the arbitrator
              that the costs of arbitration will be prohibitive as compared to
              the costs of litigation, Socius will pay as much of the
              Arbitration Fees as the arbitrator deems necessary to prevent the
              arbitration from being cost-prohibitive. Any payment of attorneys'
              fees will be governed by the AAA Rules.
            </Text>

            <Title order={3} className="text-lg font-bold mb-2">
              Confidentiality
            </Title>
            <Text className="mb-4">
              All aspects of the arbitration proceeding, and any ruling,
              decision, or award by the arbitrator, will be strictly
              confidential for the benefit of all parties.
            </Text>

            <Title order={3} className="text-lg font-bold mb-2">
              Severability
            </Title>
            <Text className="mb-4">
              If a court or the arbitrator decides that any term or provision of
              this Arbitration Agreement (other than the subsection (b) above
              titled "Prohibition of Class and Representative Actions and
              Non-Individualized Relief" above) is invalid or unenforceable, the
              parties agree to replace such term or provision with a term or
              provision that is valid and enforceable and that comes closest to
              expressing the intention of the invalid or unenforceable term or
              provision, and this Arbitration Agreement will be enforceable as
              so modified. If a court or the arbitrator decides that any of the
              provisions of subsection (b) above titled "Prohibition of Class
              and Representative Actions and Non-Individualized Relief" are
              invalid or unenforceable, then the entirety of this Arbitration
              Agreement will be null and void, unless such provisions are deemed
              to be invalid or unenforceable solely with respect to claims for
              public injunctive relief. The remainder of these Terms of Service
              will continue to apply.
            </Text>

            <Title order={3} className="text-lg font-bold mb-2">
              Future Changes to Arbitration Agreement
            </Title>
            <Text className="mb-4">
              Notwithstanding any provision in these Terms of Service to the
              contrary, Socius agrees that if it makes any future change to this
              Arbitration Agreement (other than a change to the Notice Address)
              while you are a user of the Service, you may reject any such
              change by sending Socius written notice within thirty (30)
              calendar days of the change to the Notice Address provided above.
              By rejecting any future change, you are agreeing that you will
              arbitrate any dispute between us in accordance with the language
              of this Arbitration Agreement as of the date you first accepted
              these Terms of Service (or accepted any subsequent changes to
              these Terms of Service).
            </Text>
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="termination">
          <Accordion.Control>
            <Title order={2} className="text-xl font-bold">
              Termination
            </Title>
          </Accordion.Control>
          <Accordion.Panel>
            <Text className="mb-4">
              You agree that Socius, in its sole discretion, may suspend or
              terminate your account (or any part thereof) or use of the Service
              and remove and discard any content within the Service, for any
              reason, including for lack of use or if Socius believes that you
              have violated or acted inconsistently with the letter or spirit of
              these Terms of Service. Any suspected fraudulent, abusive, or
              illegal activity that may be grounds for termination of your use
              of the Service, may be referred to appropriate law enforcement
              authorities. Socius may also in its sole discretion and at any
              time discontinue providing the Service, or any part thereof, with
              or without notice. You agree that any termination of your access
              to the Service under any provision of these Terms of Service may
              be effected without prior notice, and acknowledge and agree that
              Socius may immediately deactivate or delete your account and all
              related information and files in your account and/or bar any
              further access to such files or the Service. Further, you agree
              that Socius and Socius Affiliates will not be liable to you or any
              third party for any termination of your access to the Service.
            </Text>
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="userDisp">
          <Accordion.Control>
            <Title order={2} className="text-xl font-bold">
              User Disputes
            </Title>
          </Accordion.Control>
          <Accordion.Panel>
            <Text className="mb-4">
              You agree that you are solely responsible for your interactions
              with any other user in connection with the Service, and Socius and
              Socius Affiliates will have no liability or responsibility with
              respect thereto. Socius reserves the right, but has no obligation,
              to become involved in any way with disputes between you and any
              other user of the Service.
            </Text>
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="privacyPolicy">
          <Accordion.Control>
            <Title order={2} className="text-xl font-bold">
              Privacy Policy
            </Title>
          </Accordion.Control>
          <Accordion.Panel>
            <Text className="mb-4">
                Socius is committed to protecting your privacy. This Privacy Policy describes how we collect,
                use, and share personal information when you interact with the Site and the Services.
            </Text>
            <Box className="mb-4">
                <Title order={3} className="text-lg font-bold mb-2">
                  1. Contact Information
                </Title>
                <Text className="mb-4">
                    If you have any questions about this policy or our data practices, please contact us at:<br/><br/>
                    <b>Socius Legal Corporation</b><br/>
                    Email: support@sociuslegal.com
                </Text>
            </Box>
            <Box className="mb-4">
                <Title order={3} className="text-lg font-bold mb-2">
                  2. Personal Data We Collect
                </Title>
                <Text className="mb-4">
                    “Personal Data” refers to information that can identify you, such as:
                    <List withPadding className={dottedListStyle.dottedList}>
                        <List.Item>Name, email address, and contact details</List.Item>
                        <List.Item>Professional title and company name</List.Item>
                        <List.Item>Any content you submit via forms, email, or file uploads</List.Item>
                    </List>
                    We also collect “Anonymous Data,” which cannot be linked to your identity.
                </Text>
            </Box>
            <Box className="mb-4">
                <Title order={3} className="text-lg font-bold mb-2">
                  3. How We Collect Information
                </Title>
                <Text className="mb-4">
                  <List withPadding className={dottedListStyle.dottedList}>
                    <List.Item><b>Directly from you</b> (e.g., form submissions, inquiries, downloads)</List.Item>
                    <List.Item><b>Automatically</b> via cookies and analytics tools (e.g., IP address, browser type, device, pages visited)</List.Item>
                  </List>
                </Text>
            </Box>
            <Box className="mb-4">
                <Title order={3} className="text-lg font-bold mb-2">
                  4. Use of Personal Data
                </Title>
                <Text className="mb-4">
                    We use your information to:
                    <List withPadding className={dottedListStyle.dottedList}>
                        <List.Item>Provide and improve our Services</List.Item>
                        <List.Item>Respond to your inquiries or support requests</List.Item>
                        <List.Item>Share relevant updates, newsletters, or product information (with your consent)</List.Item>
                        <List.Item>Analyze usage trends and improve our offerings</List.Item>
                    </List>
                </Text>
            </Box>
            <Box className="mb-4">
                <Title order={3} className="text-lg font-bold mb-2">
                  5. Data Retention &amp; Security
                </Title>
                <Text className="mb-4">
                    We retain your data only as long as necessary to fulfill our services or legal obligations.
                    Socius uses industry-standard encryption and safeguards to protect your data in transit and at
                    rest.<br/>
                    We do <b>not</b> train AI models on your data. Ever.
                </Text>
            </Box>
            <Box className="mb-4">
                <Title order={3} className="text-lg font-bold mb-2">
                  6. Sharing of Information
                </Title>
                <Text className="mb-4">
                    We do <b>not sell</b> your data.<br/>
                    We may share your data with:
                    <List withPadding className={dottedListStyle.dottedList}>
                        <List.Item>Trusted service providers (e.g., analytics, hosting, support) under confidentiality agreements</List.Item>
                        <List.Item>Legal authorities when required by law</List.Item>
                        <List.Item>A buyer in connection with an acquisition or business transfer</List.Item>
                    </List>
                </Text>
            </Box>
            <Box className="mb-4">
                <Title order={3} className="text-lg font-bold mb-2">
                  7. Cookies &amp; Tracking Technologies
                </Title>
                <Text className="mb-4">
                    We use cookies and similar tools (e.g., Google Analytics) to understand how users interact with
                    our site and to improve functionality. You can disable cookies via your browser settings.<br/><br/>
                    We do <b>not</b> honor “Do Not Track” browser signals at this time.
                </Text>
            </Box>
            <Box className="mb-4">
                <Title order={3} className="text-lg font-bold mb-2">
                  8. International Transfers
                </Title>
                <Text className="mb-4">
                    If you access our Services outside the U.S., your data may be transferred to and processed in
                    the United States. We ensure any such transfers are subject to appropriate safeguards.
                </Text>
            </Box>
            <Box className="mb-4">
                <Title order={3} className="text-lg font-bold mb-2">
                  9. Children’s Privacy
                </Title>
                <Text className="mb-4">
                    Our Services are intended for professionals and are not directed at children under 13. We do
                    not knowingly collect data from minors.
                </Text>
            </Box>
            <Box className="mb-4">
                <Title order={3} className="text-lg font-bold mb-2">
                  10. Your Rights
                </Title>
                <Text className="mb-4">
                    Depending on your location, you may have the right to:
                    <List withPadding className={dottedListStyle.dottedList}>
                        <List.Item>Request access or deletion of your data</List.Item>
                        <List.Item>Update or correct inaccurate data</List.Item>
                        <List.Item>Object to or restrict data processing</List.Item>
                        <List.Item>Withdraw consent (if processing is based on consent)</List.Item>
                    </List>
                    To exercise any of these rights, please contact support@sociuslegal.com.
                </Text>
            </Box>
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="general">
          <Accordion.Control>
            <Title order={2} className="text-xl font-bold">
              General
            </Title>
          </Accordion.Control>
          <Accordion.Panel>
            <Text className="mb-4">
              These Terms of Service (together with the terms incorporated by
              reference herein) constitute the entire agreement between you and
              Socius governing your access and use of the Service, and supersede
              any prior agreements between you and Socius with respect to the
              Service. You also may be subject to additional terms and
              conditions that may apply when you use Third-Party Services,
              third-party content or third-party software. These Terms of
              Service will be governed by the laws of the State of New York
              without regard to its conflict of law provisions. With respect to
              any disputes or claims not subject to arbitration, as set forth
              above, you and Socius submit to the personal and exclusive
              jurisdiction of the state and federal courts located within the
              State of New York. The failure of Socius to exercise or enforce
              any right or provision of these Terms of Service will not
              constitute a waiver of such right or provision. If any provision
              of these Terms of Service is found by a court of competent
              jurisdiction to be invalid, the parties nevertheless agree that
              the court should endeavor to give effect to the parties'
              intentions as reflected in the provision, and the other provisions
              of these Terms of Service remain in full force and effect. You
              agree that regardless of any statute or law to the contrary, any
              claim or cause of action arising out of or related to use of the
              Service or these Terms of Service must be filed within one (1)
              year after such claim or cause of action arose or be forever
              barred. A printed version of these Terms of Service and of any
              notice given in electronic form will be admissible in judicial or
              administrative proceedings based upon or relating to these Terms
              of Service to the same extent and subject to the same conditions
              as other business documents and records originally generated and
              maintained in printed form. You may not assign these Terms of
              Service without the prior written consent of Socius, but Socius
              may assign or transfer these Terms of Service, in whole or in
              part, without restriction. The section titles in these Terms of
              Service are for convenience only and have no legal or contractual
              effect. As used in these Terms of Service, the words "include" and
              "including," and variations thereof, will not be deemed to be
              terms of limitation, but rather will be deemed to be followed by
              the words "without limitation." Notices to you may be made via
              either email or regular mail. The Service may also provide notices
              to you of changes to these Terms of Service or other matters by
              displaying notices or links to notices generally on the Service.
              Socius will not be in default hereunder by reason of any failure
              or delay in the performance of its obligations where such failure
              or delay is due to civil disturbances, riot, epidemic,
              hostilities, war, terrorist attack, embargo, natural disaster,
              acts of God, flood, fire, sabotage, fluctuations or unavailability
              of electrical power, network access or equipment, or any other
              circumstances or causes beyond Socius's reasonable control.
            </Text>
            <Text className="mb-4">
              Questions? Concerns? Suggestions? Please contact us at
              support@sociuslegal.com to report any violations of these Terms of
              Service or to pose any questions regarding these Terms of Service
              or the Service.
            </Text>
            <Text className="mb-4 text-center">* * *</Text>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>

      <Box className="flex justify-center mt-8">
        <Button onClick={scrollToTop} className="mb-4">
          Back to Top
        </Button>
      </Box>
    </Container>
  );
};

export default TermsOfService;
